// What Congress did to a budget line, stage by stage: the request, each committee's mark with its change
// and reasons, chamber passage, and the stages not yet reached. RDT&E marks cover the whole program
// element; a reason whose words belong to one project is flagged as possibly that project's.
import type { CongressMark } from "../api/types";
import { money } from "../lib/format";

function Change({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  if (Math.abs(value) < 0.0005) return <span className="chg">No change</span>;
  return <span className={`chg ${value > 0 ? "up" : "down"}`}>{`${value > 0 ? "+" : "−"}${money(Math.abs(value))}`}</span>;
}

export function CongressTimeline({ marks }: { marks: CongressMark[] }) {
  const reached = marks.filter((m) => !m.pending);
  if (!reached.length) return null;
  const request = reached[0]!.request;
  const peLevel = reached.some((m) => m.level === "pe");
  return (
    <>
      <h4>Congress</h4>
      {peLevel ? (
        <p className="muted-s cg-note">
          Congress marks the whole program element{reached[0]!.title ? ` (${reached[0]!.title.toLowerCase()})` : ""}, not this project alone.
        </p>
      ) : null}
      <ol className="congress" id="congress">
        <li className="cg-stage">
          <span className="cg-dot" aria-hidden="true" />
          <div className="cg-body">
            <div className="cg-head">
              <b>President’s request</b>
              <span className="cg-amt">{money(request)}</span>
            </div>
          </div>
        </li>
        {marks.map((m) =>
          m.pending ? (
            <li key={m.committee} className="cg-stage pending">
              <span className="cg-dot" aria-hidden="true" />
              <div className="cg-body">
                <div className="cg-head">
                  <b>{m.name}</b>
                  <span className="cg-amt">Not yet</span>
                </div>
              </div>
            </li>
          ) : (
            <li key={m.committee} className={`cg-stage ${m.change && Math.abs(m.change) >= 0.0005 ? "moved" : ""}`}>
              <span className="cg-dot" aria-hidden="true" />
              <div className="cg-body">
                <div className="cg-head">
                  <b>{m.name}</b>
                  <span className="cg-amt">
                    {money(m.recommended)} <Change value={m.change} />
                  </span>
                </div>
                {m.same_as ? (
                  <p className="muted-s">Passed as the {m.same_as} marked it.</p>
                ) : m.reasons?.length ? (
                  <ul className="cg-reasons">
                    {m.reasons.map((r, i) => (
                      <li key={i} className={r.project && !r.this_project ? "other" : ""}>
                        <span>{r.text}</span>
                        {r.amount != null ? <span className="cg-r-amt">{`${r.amount > 0 ? "+" : "−"}${money(Math.abs(r.amount))}`}</span> : null}
                        {r.this_project ? <span className="strength possible">Possibly this project</span> : null}
                        {r.project && !r.this_project ? <small> likely project {r.project}</small> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {m.source_url ? (
                  <a className="cg-src" href={m.source_url} target="_blank" rel="noopener noreferrer">
                    {m.source}
                    {m.anchor ? ` · ${m.anchor}` : ""}
                    {m.line ? ` · line ${m.line}` : ""}
                  </a>
                ) : null}
              </div>
            </li>
          ),
        )}
      </ol>
    </>
  );
}
